import { Link } from 'react-router-dom'

const STEPS = [
  {
    num: '01',
    title: 'Compléter votre profil',
    icon: '👤',
    content: [
      'Rendez-vous dans <strong>Mon profil</strong> pour renseigner votre photo, votre bio et votre zone géographique.',
      'Activez la <strong>géolocalisation</strong> : les missions vous sont proposées selon votre position GPS. Sans coordonnées, vous recevez toutes les missions sans filtre de distance.',
      'Définissez votre <strong>rayon d\'intervention</strong> maximum (en km) pour ne voir que les missions dans votre zone.',
    ],
  },
  {
    num: '02',
    title: 'Déclarer vos qualifications',
    icon: '📄',
    content: [
      'Dans <strong>Qualifications</strong>, ajoutez vos spécialités : ADS, SSIAP 1/2/3, Formateur SST, etc.',
      'Uploadez vos documents justificatifs (carte professionnelle, diplômes). Ils sont vérifiés par l\'équipe Velink.',
      'Les missions filtrées par spécialité ne s\'affichent qu\'aux prestataires qualifiés. Plus votre profil est complet, plus vous recevez de missions pertinentes.',
    ],
  },
  {
    num: '03',
    title: 'Activer les notifications push',
    icon: '🔔',
    content: [
      'Téléchargez l\'<strong>application mobile Velink</strong> (disponible sur App Store et Google Play).',
      'Autorisez les notifications push lors du premier lancement. Un token Firebase est alors associé à votre profil.',
      'Chaque nouvelle mission à proximité génère une notification instantanée avec le montant, la date et la ville.',
    ],
  },
  {
    num: '04',
    title: 'Consulter et accepter une mission',
    icon: '✅',
    content: [
      'Dans <strong>Missions disponibles</strong>, vous voyez toutes les prestations ouvertes correspondant à votre profil, triées par distance.',
      'Cliquez sur une mission pour voir les détails : description, montant net, date, adresse, entreprise donneuse d\'ordre.',
      '<strong>Accepter</strong> verrouille la mission — elle disparaît de la liste des autres prestataires. <strong>Décliner</strong> vous permet de passer à la suite.',
    ],
  },
  {
    num: '05',
    title: 'Réaliser la mission',
    icon: '🎯',
    content: [
      'Retrouvez vos missions acceptées dans <strong>Mes missions</strong>. Vous pouvez y échanger des messages avec l\'entreprise via le chat intégré.',
      'Présentez-vous à l\'heure et au lieu convenus. En cas d\'imprévu, contactez l\'entreprise directement via la messagerie.',
      'À la fin de la mission, l\'entreprise confirme la réalisation et déclenche la libération du paiement.',
    ],
  },
  {
    num: '06',
    title: 'Recevoir votre paiement',
    icon: '💶',
    content: [
      'Le paiement est sécurisé en <strong>escrow</strong> dès que l\'entreprise publie la mission. Il est libéré à la confirmation.',
      'Vous recevez le virement sur votre compte Stripe Connect (lié à votre IBAN) ou votre compte PayPal selon le choix de l\'entreprise.',
      '<strong>Velink ne prélève aucune commission côté prestataire.</strong> Vous recevez 100 % du montant affiché.',
    ],
  },
]

const FAQ = [
  { q: 'Combien coûte Velink pour un prestataire ?', a: 'Rien. Velink est gratuit pour les prestataires. La commission de 10 % (à partir de la 2e mission) est prélevée uniquement côté entreprise.' },
  { q: 'Que se passe-t-il si je décline trop de missions ?', a: 'Il n\'y a pas de pénalité pour avoir décliné des missions. Cela n\'affecte pas votre profil ni votre visibilité.' },
  { q: 'Comment améliorer mon score de notation ?', a: 'Soyez ponctuel, professionnel et répondez rapidement aux messages. Les entreprises notent la prestation après la mission. La note impacte votre positionnement dans les futurs matchings.' },
  { q: 'Puis-je travailler avec plusieurs entreprises simultanément ?', a: 'Oui, tant que les dates ne se chevauchent pas. Vous êtes libre d\'accepter autant de missions que vous souhaitez.' },
  { q: 'Que faire si l\'entreprise ne confirme pas la mission ?', a: 'Contactez le support Velink (contact@velink.fr). Un litige peut être ouvert et le paiement escrow est maintenu pendant 30 jours.' },
]

export default function GuidePrestatairePage() {
  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-success/10 border border-success/20 text-success text-xs font-medium mb-4">
          Guide prestataire — Démarrer en 6 étapes
        </div>
        <h1 className="text-2xl font-bold text-text-primary mb-2">Comment utiliser Velink</h1>
        <p className="text-text-secondary text-sm leading-relaxed max-w-2xl">
          Velink vous met en relation avec des entreprises qui cherchent des intervenants qualifiés pour leurs événements et formations.
          Ce guide vous accompagne de l'inscription à votre premier virement.
        </p>
      </div>

      {/* Commission banner */}
      <div className="glass-card p-5 rounded-xl border-l-2 border-l-success flex items-start gap-4">
        <span className="text-2xl">💸</span>
        <div>
          <p className="text-text-primary font-semibold text-sm mb-1">0 % de commission pour les prestataires</p>
          <p className="text-text-secondary text-sm">Vous recevez 100 % du montant affiché. La plateforme est entièrement gratuite pour vous.</p>
        </div>
      </div>

      {/* Steps */}
      <div className="space-y-4">
        {STEPS.map((step) => (
          <div key={step.num} className="glass-card rounded-xl overflow-hidden">
            <div className="flex items-center gap-4 p-5">
              <div className="w-10 h-10 rounded-xl bg-accent/15 border border-accent/25 flex items-center justify-center flex-shrink-0">
                <span className="text-lg">{step.icon}</span>
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-1">
                  <span className="text-xs font-mono text-text-muted">Étape {step.num}</span>
                </div>
                <h2 className="text-text-primary font-semibold">{step.title}</h2>
              </div>
            </div>
            <div className="px-5 pb-5 border-t border-white/[0.06] pt-4">
              <ul className="space-y-2">
                {step.content.map((line, i) => (
                  <li key={i} className="flex items-start gap-2 text-text-secondary text-sm leading-relaxed">
                    <span className="text-accent mt-0.5 flex-shrink-0">→</span>
                    <span dangerouslySetInnerHTML={{ __html: line }} />
                  </li>
                ))}
              </ul>
            </div>
          </div>
        ))}
      </div>

      {/* Disponibilités */}
      <div className="glass-card p-5 rounded-xl">
        <h2 className="text-text-primary font-semibold mb-3 flex items-center gap-2">
          <span>📅</span> Gérer vos disponibilités
        </h2>
        <p className="text-text-secondary text-sm leading-relaxed mb-3">
          Dans <strong className="text-text-primary">Mes disponibilités</strong>, indiquez les plages horaires où vous êtes joignable.
          Cela aide les entreprises à filtrer leurs recherches et améliore votre taux de match.
        </p>
        <Link to="/disponibilites" className="glass-btn px-4 py-2 text-sm rounded-xl inline-block">
          Configurer mes disponibilités →
        </Link>
      </div>

      {/* FAQ */}
      <div>
        <h2 className="text-text-primary font-semibold mb-4">Questions fréquentes</h2>
        <div className="space-y-3">
          {FAQ.map((item, i) => (
            <div key={i} className="glass-card p-5 rounded-xl">
              <p className="text-text-primary font-medium text-sm mb-2">{item.q}</p>
              <p className="text-text-secondary text-sm leading-relaxed">{item.a}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Contact */}
      <div className="glass-card p-5 rounded-xl flex items-start gap-4">
        <span className="text-2xl">✉️</span>
        <div>
          <p className="text-text-primary font-semibold text-sm mb-1">Besoin d'aide ?</p>
          <p className="text-text-secondary text-sm">
            Contactez notre équipe à{' '}
            <a href="mailto:contact@velink.fr" className="text-accent hover:underline">contact@velink.fr</a>.
            Nous répondons sous 24 h (jours ouvrés).
          </p>
        </div>
      </div>
    </div>
  )
}
